import { getMyPermissionAccess } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { stateful, useMemo, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import {
  subscribePartnerSchedulingClientSiteCounts,
  subscribePartnerSchedulingClientSitesForClient,
  subscribePartnerSchedulingClientStatus,
  subscribePartnerSchedulingPartnersForNames,
} from '../../api/partner-scheduling-workbook-subscriptions.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { normalizeSchedulingKey } from '@nsc/workflows-partner-scheduling/ticketing/partner-scheduling-patterns.js';
import { html, nothing } from 'lit';
import { useNexusPermissionAccess } from '../../features/permissions/use-nexus-permission.js';
import {
  indexPartnersByKey,
  PartnerSchedulingClientSitesModal,
  type ClientSiteRecord,
  type PartnerContact,
} from './PartnerSchedulingClientSitesModal.js';
import { PartnerSchedulingAllowedQueuesSection } from './PartnerSchedulingAllowedQueuesSection.js';

function siteCountsByClientKey(
  rows: Array<{ clientKey?: string | null; siteCount?: number | null }> | undefined,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows ?? []) {
    const key = normalizeSchedulingKey(row.clientKey ?? '');
    if (!key) continue;
    counts.set(key, row.siteCount ?? 0);
  }
  return counts;
}

function partnerNamesFromSites(sites: ClientSiteRecord[] | undefined): string[] {
  const names = new Set<string>();
  for (const site of sites ?? []) {
    for (const name of site.partners ?? []) {
      const trimmed = name?.trim();
      if (trimmed) names.add(trimmed);
    }
  }
  return [...names];
}

export const PartnerSchedulingClientsPage = stateful(() => {
  const [filter, setFilter] = useState('');
  const [selectedClientKey, setSelectedClientKey] = useState<string | null>(null);

  const { can: canImport } = useNexusPermissionAccess(NexusPermission.PartnerSchedulingImport);
  const toggleAccess = useQuery(
    () =>
      getMyPermissionAccess(NexusPermission.PartnerSchedulingClientToggle)({
        hasAccess: 1,
        allowedContexts: 1,
      }),
    [],
  ).value;
  // Only Import is global. Empty Toggle scope must not unlock all clients (ABAC isGlobal is misleading here).
  const isGlobalToggle = canImport;
  const allowedKeys = new Set(
    (toggleAccess?.allowedContexts ?? []).map(key => normalizeSchedulingKey(String(key ?? ''))).filter(Boolean),
  );
  const canToggleKey = (clientKey: string) => !!clientKey && (isGlobalToggle || allowedKeys.has(clientKey));

  const clients = useObservable(
    () =>
      subscribePartnerSchedulingClientStatus()({
        _id: 1,
        _class: 1,
        clientName: 1,
        clientKey: 1,
        enabled: 1,
      }),
    [],
  );
  const siteCounts = useObservable(
    () => subscribePartnerSchedulingClientSiteCounts()({ clientKey: 1, siteCount: 1 }),
    [],
  );
  const clientSites = useObservable<ClientSiteRecord[]>(
    () =>
      subscribePartnerSchedulingClientSitesForClient(selectedClientKey ?? '')({
        clientKey: 1,
        clientName: 1,
        region: 1,
        country: 1,
        city: 1,
        address: 1,
        zipCode: 1,
        partners: 1,
      }),
    [selectedClientKey],
  );
  const partnerNames = useMemo(() => [partnerNamesFromSites(clientSites)], [clientSites]);
  const partners = useObservable<PartnerContact[]>(
    () =>
      subscribePartnerSchedulingPartnersForNames(partnerNames)({
        partnerName: 1,
        contactEmail: 1,
      }),
    [selectedClientKey, (partnerNames ?? []).join('\0')],
  );

  const siteCountByKey = siteCountsByClientKey(siteCounts);
  const partnersByKey = indexPartnersByKey(partners);
  const normalizedFilter = filter.trim().toLowerCase();
  const rows = (clients ?? [])
    .filter(row => {
      const clientKey = normalizeSchedulingKey(row.clientKey || row.clientName || '');
      if (!clientKey) return false;
      if (!isGlobalToggle && !allowedKeys.has(clientKey)) return false;
      if (!normalizedFilter) return true;
      return [row.clientName, row.clientKey].filter(Boolean).join(' ').toLowerCase().includes(normalizedFilter);
    })
    .sort((a, b) => (a.clientName ?? a.clientKey ?? '').localeCompare(b.clientName ?? b.clientKey ?? ''));

  const selectedClient =
    selectedClientKey != null
      ? ((clients ?? []).find(c => normalizeSchedulingKey(c.clientKey || c.clientName || '') === selectedClientKey) ??
        null)
      : null;

  const goBack = () => {
    if (globalThis.history.length > 1) globalThis.history.back();
    else globalThis.location.assign('/');
  };

  return html`
    <div class="min-h-screen bg-base-100 font-sans p-6">
      <div class="max-w-5xl mx-auto flex flex-col gap-6">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex flex-wrap items-center gap-4">
            <button type="button" class="btn btn-ghost btn-sm" @click=${goBack}>← Back</button>
            <h1 class="text-2xl font-extrabold text-neutral">Partner scheduling</h1>
          </div>
          ${canImport
            ? html`<a class="btn btn-primary btn-sm" href="/partner-scheduling/import">Import workbook</a>`
            : nothing}
        </div>

        ${canImport ? PartnerSchedulingAllowedQueuesSection({ canManage: true }) : nothing}

        <section class="card bg-base-100 shadow-xl rounded-2xl border border-neutral/10">
          <div class="card-body gap-4">
            <p class="text-base text-base-content/70">
              Client names from the partner workbook import. Enable a client to allow partner scheduling mail processing
              and retries for tickets with that client name.
            </p>
            <input
              type="search"
              class="input input-bordered w-full max-w-md"
              placeholder="Filter by client name…"
              .value=${filter}
              @input=${(e: Event) => setFilter((e.target as HTMLInputElement).value)}
            />
            <div class="overflow-x-auto">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Enabled</th>
                    <th>Client name</th>
                    <th>Workbook sites</th>
                  </tr>
                </thead>
                <tbody>
                  ${rows.length === 0
                    ? html`<tr>
                        <td colspan="3" class="text-center text-base-content/60 py-8">
                          ${canImport
                            ? html`No clients yet —
                                <a class="link link-primary" href="/partner-scheduling/import">import a workbook</a>
                                to populate this list.`
                            : html`No partner-scheduling clients assigned to your account.`}
                        </td>
                      </tr>`
                    : rows.map(row => {
                        const clientKey = normalizeSchedulingKey(row.clientKey || row.clientName || '');
                        const siteCount = siteCountByKey.get(clientKey) ?? 0;
                        const canToggle = canToggleKey(clientKey);
                        return html`
                          <tr class="hover">
                            <td>
                              <input
                                type="checkbox"
                                class="toggle toggle-sm toggle-primary"
                                ?disabled=${!canToggle}
                                .checked=${row.enabled === true}
                                @change=${() => {
                                  if (!canToggle) return;
                                  row.enabled = row.enabled !== true;
                                  void row.save();
                                }}
                              />
                            </td>
                            <td>${row.clientName ?? row.clientKey ?? '—'}</td>
                            <td>
                              <button
                                type="button"
                                class="btn btn-ghost btn-xs font-semibold text-primary"
                                ?disabled=${siteCount === 0}
                                @click=${() => setSelectedClientKey(clientKey)}
                              >
                                ${siteCount}
                              </button>
                            </td>
                          </tr>
                        `;
                      })}
                </tbody>
              </table>
            </div>
            <p class="text-sm text-base-content/50">${rows.length} client(s)</p>
          </div>
        </section>
      </div>

      ${PartnerSchedulingClientSitesModal({
        client: selectedClient,
        sites: [...(clientSites ?? [])].sort((a, b) =>
          [a.region, a.country, a.city, a.address]
            .join('\0')
            .localeCompare([b.region, b.country, b.city, b.address].join('\0')),
        ),
        partnersByKey,
        onClose: () => setSelectedClientKey(null),
      })}
    </div>
  `;
});
